import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useReveal, useFloatBtn, useCounter } from '../components/hooks';
import Footer from '../components/Footer';
import './Home.css';

const testimonials = [
  { res: '−12kg', stars: '★★★★★', txt: '"I\'d tried everything before Liam. Within 8 weeks I\'d lost 12kg and actually understood why my body was changing. The nutrition plan alone was a game-changer."', initials: 'JM', name: 'James M.', sub: 'FORGE Client · Online' },
  { res: '+8kg muscle', stars: '★★★★★', txt: '"As a woman I was worried about getting \'too bulky\' — Liam completely changed my mindset and my body. I\'ve built lean muscle I\'m genuinely proud of."', initials: 'SR', name: 'Sophie R.', sub: 'IGNITE Client · Online' },
  { res: '−18lbs', stars: '★★★★★', txt: '"The WhatsApp access on the FORGE package is what makes it. Liam replied within minutes every time. That accountability is what kept me going when I wanted to quit."', initials: 'DK', name: 'Daniel K.', sub: 'FORGE Client · In-Person & Online' },
  { res: 'Full recomp', stars: '★★★★★', txt: '"I\'m 47 years old and in the best shape of my life. Liam never made me feel like age was a barrier — he just adapted the plan and delivered results."', initials: 'AL', name: 'Amanda L.', sub: 'FORGE Client · Online' },
  { res: '−10kg', stars: '★★★★★', txt: '"Started with IGNITE to test the water — honestly could\'ve gone straight to FORGE. The quality of the plan and the check-ins blew me away for the price."', initials: 'TC', name: 'Tom C.', sub: 'IGNITE → FORGE Client' },
];

const faqs = [
  { q: 'Do I need a gym?', a: 'Absolutely not. Your plan is built around whatever equipment you have — full gym, home setup, resistance bands, or bodyweight only. Both options are fully covered.' },
  { q: 'Can I do this if I\'m a complete beginner?', a: 'Yes — Liam coaches all experience levels, from first-timers to experienced lifters who\'ve hit a plateau. The plan meets you exactly where you are right now.' },
  { q: 'What if I\'m not local — can I still join?', a: '100%. The full coaching experience — training, nutrition, check-ins, and support — is available entirely online. Location is never a barrier.' },
  { q: 'How quickly will I see results?', a: 'Most clients notice measurable changes within 3–4 weeks. Visible, photograph-worthy transformation typically builds from week 6 onward. Consistent effort = consistent results.' },
  { q: 'What\'s the difference between IGNITE and FORGE?', a: 'IGNITE gives you a premium custom programme and nutrition plan with weekly check-ins via the client portal. FORGE adds direct WhatsApp access to Liam, weekly video calls, deeper tracking, and in-person session discounts if you\'re local.' },
  { q: 'Is there a money-back guarantee?', a: 'Yes — both packages include a full refund within 14 days, no questions asked. After that, if you follow the programme 100% and don\'t see results, Liam will continue coaching you free of charge until you do.' },
  { q: 'Can I cancel anytime?', a: 'Yes — both packages are rolling monthly subscriptions. No lock-in contracts, no cancellation fees. Stay for as long as you\'re getting results — which, if you put in the work, will be a long time.' },
];

function FAQ() {
  const [openIdx, setOpenIdx] = useState(null);
  return (
    <section id="faq">
      <div className="s-label rev">Got Questions</div>
      <h2 className="s-title rev">EVERYTHING YOU<br />NEED TO KNOW</h2>
      <div className="faq-grid">
        <div className="faq-left rev-l">
          <p>Investing in yourself is a big decision — and it should be. Here are the questions I hear most often before people take the leap. Don't see yours? Hit the button and I'll reply personally.</p>
          <a href="#cta" className="btn-gold">Ask Liam Directly</a>
        </div>
        <div className="faq-items rev-r">
          {faqs.map((f, i) => (
            <div className={`faq-item${openIdx === i ? ' open' : ''}`} key={i}>
              <div className="faq-q" onClick={() => setOpenIdx(openIdx === i ? null : i)}>
                {f.q} <span className="faq-ico">+</span>
              </div>
              <div className="faq-a"><p>{f.a}</p></div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

export default function Home() {
  useReveal();
  useFloatBtn();
  useCounter('stats');

  return (
    <>
      {/* HERO */}
      <section id="hero">
        <div className="hero-bg"></div>
        <div className="hero-grid"></div>
        <div className="hero-tag">
          <div className="hero-tag-line"></div>
          <span>Personal Coaching · Bath, UK · Worldwide Online</span>
        </div>
        <h1 className="hero-title">
          BURN<br />
          <span className="accent">FAT.</span><br />
          BUILD<br />
          <span className="ghost">MUSCLE.</span>
        </h1>
        <p className="hero-sub">Real plans. Real accountability. Real results. Liam W Coaching delivers bespoke training and nutrition programmes built entirely around you — your body, your goals, your life.</p>
        <div className="hero-proof">
          <div className="hero-proof-avatars">
            <span>JM</span><span>SR</span><span>DK</span><span>AL</span>
          </div>
          <p><strong>200+</strong> transformations &amp; counting</p>
        </div>
        <div className="hero-actions">
          <Link to="/packages" className="btn-gold">Start Your Transformation →</Link>
          <Link to="/packages" className="btn-outline">View Packages</Link>
        </div>
        <div className="scroll-hint">
          <span>Scroll</span>
          <div className="scroll-line"></div>
        </div>
      </section>

      {/* STATS */}
      <div id="stats">
        {[
          { t: 200, lbl: 'Clients Transformed' },
          { t: 100, lbl: 'Satisfaction Rate' },
          { t: 5, lbl: 'Years Coaching' },
          { t: 50, lbl: 'Online Countries' },
        ].map(({ t, lbl }) => (
          <div className="stat-item" key={lbl}>
            <div className="stat-num" data-t={t}>0{t === 100 ? '%' : '+'}</div>
            <div className="stat-lbl">{lbl}</div>
          </div>
        ))}
      </div>

      {/* ABOUT SNIPPET */}
      <section id="about">
        <div className="about-visual rev-l">
          <div className="about-frame">
            <div className="about-initials">LW</div>
          </div>
          <div className="about-corner"></div>
          <div className="about-badge">Level 4<br />Strength &amp; Conditioning<br />Coach</div>
        </div>
        <div className="about-content">
          <div className="s-label rev">The Coach</div>
          <h2 className="s-title rev">LIAM W —<br />YOUR COACH,<br />YOUR ALLY.</h2>
          <p className="rev">Liam is a CIMSPA-endorsed, OFQUAL-regulated Level 4 Strength &amp; Conditioning Coach based in Bath, UK — coaching clients worldwide online.</p>
          <p className="rev">No templates. No guesswork. Every client gets a programme built from scratch around their body, goals, and lifestyle.</p>
          <ul className="about-creds">
            <li className="rev">Level 2 Gym Instructor — CIMSPA Endorsed</li>
            <li className="rev">Level 3 Personal Trainer — OFQUAL Regulated</li>
            <li className="rev">Level 4 Strength &amp; Conditioning Coach</li>
            <li className="rev">200+ client transformations</li>
          </ul>
        </div>
      </section>

      {/* PILLARS */}
      <section id="pillars">
        <div className="s-label rev" style={{ justifyContent: 'center' }}>What You Get</div>
        <h2 className="s-title rev">THE FULL<br />COACHING SYSTEM</h2>
        <p className="sub rev">Six pillars that work together to transform your body and keep it transformed.</p>
        <div className="pillars-grid">
          {[
            { icon: '🏋️', title: 'Custom Training', text: 'A fully bespoke programme built around your equipment, schedule, and experience. No off-the-shelf plans.' },
            { icon: '🥗', title: 'Nutrition Strategy', text: 'Real food, real macros, real flexibility. A strategy you can actually live with — not a crash diet.' },
            { icon: '📊', title: 'Progress Tracking', text: 'Weekly check-ins, body composition analysis, and plan adjustments based on your real data.' },
            { icon: '🧠', title: 'Mindset Coaching', text: 'The mental side of transformation is just as important as the physical. Liam coaches both.' },
            { icon: '💬', title: 'Direct Access', text: 'FORGE clients get Liam\'s WhatsApp number. Not a chatbot. Not a support team. Liam himself.' },
            { icon: '⚡', title: 'Results Guarantee', text: 'Follow the programme 100% and don\'t get results? Liam keeps coaching you for free until you do.' },
          ].map((p, i) => (
            <div className="pillar rev" key={i} style={{ transitionDelay: `${i * 0.08}s` }}>
              <span className="pillar-icon">{p.icon}</span>
              <h3>{p.title}</h3>
              <p>{p.text}</p>
              <div className="pillar-n">0{i + 1}</div>
            </div>
          ))}
        </div>
      </section>

      {/* WHO */}
      <section id="who">
        <div className="who-content rev-l">
          <div className="s-label">Who This Is For</div>
          <h2 className="s-title">THIS IS FOR<br />YOU IF...</h2>
          <p>Liam W Coaching works for men and women of all ages — whether you're 18 or 55, a beginner or someone who's been training for years but stuck spinning their wheels.</p>
          <ul className="who-list">
            {['You\'re tired of trying plans that don\'t work for your body','You want to lose fat AND build muscle at the same time','You train at home, in a gym, or both — it doesn\'t matter','You\'re local and want in-person sessions, or fully online','You want real accountability, not a PDF you forget about','You\'re ready to commit and actually see results this time'].map((item, i) => (
              <li key={i}><div className="who-check">✓</div>{item}</li>
            ))}
          </ul>
        </div>
        <div className="who-visual rev-r">
          {[{ icon: '🏠', title: 'Train Anywhere', text: 'Home, gym, or hotel room — your plan is built around your equipment and location.' },
            { icon: '🥗', title: 'Nutrition Included', text: 'Every package includes a full bespoke nutrition plan — not just macros, a real eating strategy.' },
            { icon: '📍', title: 'In-Person Option', text: 'Local to Liam? Book face-to-face sessions for hands-on coaching and form correction.' },
            { icon: '🌍', title: 'Fully Online', text: 'Not local? No problem. The full coaching experience is available entirely online.' },
          ].map((c, i) => (
            <div className="who-card" key={i}>
              <span className="who-card-icon">{c.icon}</span>
              <h4>{c.title}</h4>
              <p>{c.text}</p>
            </div>
          ))}
        </div>
      </section>

      {/* PACKAGES PREVIEW */}
      <section id="packages">
        <div className="pkg-header">
          <div>
            <div className="s-label rev">Choose Your Level</div>
            <h2 className="s-title rev">COACHING<br />PACKAGES</h2>
          </div>
          <p className="rev">No templates. No copy-paste plans. Every package is built entirely around your body, your goals, and your life.</p>
        </div>
        <div className="pkg-grid">
          {/* IGNITE */}
          <div className="pkg-card rev">
            <div className="pkg-tier">Starter</div>
            <div className="pkg-name">IGNITE</div>
            <div className="pkg-tagline">"The spark that changes everything."</div>
            <div className="pkg-price">
              <div className="price-big"><sup>£</sup>49.99</div>
              <div className="price-per">/ month</div>
            </div>
            <div className="price-note">Monthly subscription · Cancel anytime · No hidden fees</div>
            <div className="pkg-div"></div>
            <ul className="pkg-feats">
              {['Custom Training Programme (Gym or Home)','Bespoke Nutrition & Meal Strategy','Weekly Progress Check-In via Client Portal','Private Online Client Portal Access','Exercise Video Library & Form Guides','Supplement & Lifestyle Guidance','Full refund available within 14 days'].map(f => (
                <li key={f}><div className="fc on">✓</div>{f}</li>
              ))}
              <li><div className="fc">✗</div><span style={{ color: 'var(--muted)' }}>Direct WhatsApp Access to Liam</span></li>
              <li><div className="fc">✗</div><span style={{ color: 'var(--muted)' }}>In-Person Session Discount</span></li>
            </ul>
            <Link to="/packages" className="pkg-btn sec">Get Started with IGNITE</Link>
          </div>
          {/* FORGE */}
          <div className="pkg-card featured rev" style={{ transitionDelay: '.12s' }}>
            <div className="pkg-badge">Most Popular</div>
            <div className="pkg-tier">Elite</div>
            <div className="pkg-name">FORGE</div>
            <div className="pkg-tagline">"Total transformation. Zero compromise."</div>
            <div className="pkg-price">
              <div className="price-big" style={{ color: 'var(--gold)' }}><sup>£</sup>99.99</div>
              <div className="price-per">/ month</div>
            </div>
            <div className="price-note">Monthly subscription · Limited spots · New intake opens monthly</div>
            <div className="pkg-div" style={{ background: 'rgba(201,169,110,.18)' }}></div>
            <ul className="pkg-feats">
              {['Custom 12-Week Periodised Training Plan','Advanced Nutrition, Macros & Meal Planning','Direct WhatsApp Access to Liam (Daily Support)','Weekly 1-on-1 Video Check-In Call','Body Composition & Progress Tracking','Mindset, Habit & Lifestyle Coaching','Priority 24-Hour Response Guarantee','Discounted In-Person Sessions (if local)','Full refund available within 14 days'].map(f => (
                <li key={f}><div className="fc on">✓</div>{f}</li>
              ))}
            </ul>
            <Link to="/packages" className="pkg-btn pri">Apply For FORGE →</Link>
            <div className="pkg-glow"></div>
          </div>
        </div>
        <div className="delivery-note rev">
          <strong>📍 In-Person Coaching:</strong> If you're local to Liam, in-person training sessions are available for an additional per-session fee. FORGE clients receive an exclusive discount. <strong>🌍 Not local?</strong> The full coaching experience is delivered 100% online — same results, same accountability.
        </div>
      </section>

      {/* PROCESS */}
      <section id="process">
        <div className="process-hd">
          <div className="s-label rev">How It Works</div>
          <h2 className="s-title rev">YOUR FIRST<br />30 DAYS</h2>
        </div>
        <div className="steps">
          {[
            { n: '01', title: 'Apply', text: 'Fill out a short application so Liam can understand your body, goals, history, and equipment.' },
            { n: '02', title: 'Free Call', text: 'A free 30-minute discovery call to map your roadmap and make sure this is the right fit for you.' },
            { n: '03', title: 'Your Plan', text: 'Your fully custom training and nutrition plan is built from scratch and delivered within 48 hours.' },
            { n: '04', title: 'Transform', text: 'Execute, get coached, and watch your body and energy change week by week — for real this time.' },
          ].map((s, i) => (
            <div className="step rev" key={s.n} style={{ transitionDelay: `${i * 0.1}s` }}>
              <div className="step-n">{s.n}</div>
              <h3>{s.title}</h3>
              <p>{s.text}</p>
            </div>
          ))}
        </div>
      </section>

      {/* TESTIMONIALS */}
      <section id="testi">
        <div className="testi-hd">
          <div className="s-label" style={{ justifyContent: 'center' }}>Real People. Real Results.</div>
          <h2 className="s-title rev">WHAT CLIENTS SAY</h2>
        </div>
        <div className="testi-wrap">
          <div className="testi-track">
            {[...testimonials, ...testimonials].map((t, i) => (
              <div className="testi-card" key={i}>
                <div className="testi-res">{t.res}</div>
                <div className="testi-stars">{t.stars}</div>
                <p className="testi-txt">{t.txt}</p>
                <div className="testi-auth">
                  <div className="testi-av">{t.initials}</div>
                  <div>
                    <div className="testi-name">{t.name}</div>
                    <div className="testi-sub">{t.sub}</div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <FAQ />

      {/* FINAL CTA */}
      <section id="cta">
        <div className="cta-bg"></div>
        <div className="cta-inner">
          <div className="urgency rev">
            <div className="urgency-dot"></div>
            <span>New intake opens monthly — spots are limited</span>
          </div>
          <div className="s-label rev" style={{ justifyContent: 'center' }}><span>The Decision Is Yours</span></div>
          <h2 className="s-title rev">READY TO<br /><span style={{ color: 'var(--gold)' }}>BURN FAT,</span><br />BUILD MUSCLE?</h2>
          <p className="rev">Liam only takes on clients he can fully commit to. Apply today, get a free discovery call, and find out if you're the right fit. Spots fill fast every month.</p>
          <div className="cta-btns rev">
            <Link to="/packages" className="btn-gold">Start Your Journey Today</Link>
            <Link to="/packages" className="btn-outline">Book a Free Discovery Call</Link>
          </div>
        </div>
      </section>

      {/* SOCIAL */}
      <section id="social">
        <div className="social-left">
          <h3>FOLLOW THE JOURNEY</h3>
          <p>Transformations, tips, and behind-the-scenes coaching on Instagram.</p>
        </div>
        <a href="https://instagram.com" target="_blank" rel="noopener noreferrer" className="social-link">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <rect x="2" y="2" width="20" height="20" rx="5" ry="5" />
            <circle cx="12" cy="12" r="4" />
            <circle cx="17.5" cy="6.5" r="1" fill="currentColor" stroke="none" />
          </svg>
          Follow on Instagram
        </a>
      </section>

      <Footer />
      <Link to="/packages" className="float-btn" id="floatBtn">Apply Now →</Link>
    </>
  );
}
